import type {
  InputVariableDefinitions,
  QueryTemplatePredefinedVariable,
  QueryTemplateValueType,
} from '../types/data-connection-queries.js';

type SqlTemplateRuntimeVariable = {
  mappedVariable: QueryTemplatePredefinedVariable;
  placeholderAlias: string;
  aliases: string[];
  type: QueryTemplateValueType;
  description: string;
};

const SQL_TEMPLATE_PLACEHOLDER_PATTERN = /\$\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;

export const SQL_TEMPLATE_RUNTIME_VARIABLES: SqlTemplateRuntimeVariable[] = [
  {
    mappedVariable: '$IS_NATION',
    placeholderAlias: 'isNation',
    aliases: ['is_nation'],
    type: 'boolean',
    description: 'Whether the current user context is national access.',
  },
];

const SQL_TEMPLATE_ALIAS_TO_PREDEFINED_VARIABLE = SQL_TEMPLATE_RUNTIME_VARIABLES.reduce<
  Record<string, QueryTemplatePredefinedVariable>
>((accumulator, variable) => {
  accumulator[variable.placeholderAlias] = variable.mappedVariable;

  for (const alias of variable.aliases) {
    accumulator[alias] = variable.mappedVariable;
  }

  return accumulator;
}, {});

const SQL_TEMPLATE_VARIABLE_BY_MAPPED = SQL_TEMPLATE_RUNTIME_VARIABLES.reduce<
  Record<QueryTemplatePredefinedVariable, SqlTemplateRuntimeVariable>
>(
  (accumulator, variable) => {
    accumulator[variable.mappedVariable] = variable;
    return accumulator;
  },
  {} as Record<QueryTemplatePredefinedVariable, SqlTemplateRuntimeVariable>
);

export function extractSqlTemplatePlaceholderKeys(templateSql: string): string[] {
  const variableMatches = templateSql.matchAll(SQL_TEMPLATE_PLACEHOLDER_PATTERN);
  const uniqueKeys = new Set<string>();

  for (const match of variableMatches) {
    const variableKey = match[1];
    if (variableKey) {
      uniqueKeys.add(variableKey);
    }
  }

  return Array.from(uniqueKeys);
}

export function getSupportedSqlTemplatePlaceholderAliases(): string[] {
  return SQL_TEMPLATE_RUNTIME_VARIABLES.flatMap((variable) => [
    variable.placeholderAlias,
    ...variable.aliases,
  ]);
}

export function deriveSqlTemplateInputVariableDefinitions(data: {
  templateSql: string;
  existingDefinitions?: InputVariableDefinitions;
}): {
  placeholderKeys: string[];
  unknownPlaceholderKeys: string[];
  inputVariableDefinitions: InputVariableDefinitions;
} {
  const placeholderKeys = extractSqlTemplatePlaceholderKeys(data.templateSql);
  const inputVariableDefinitions: InputVariableDefinitions = {};
  const unknownPlaceholderKeys: string[] = [];

  for (const placeholderKey of placeholderKeys) {
    const mappedVariable = SQL_TEMPLATE_ALIAS_TO_PREDEFINED_VARIABLE[placeholderKey];
    if (!mappedVariable) {
      unknownPlaceholderKeys.push(placeholderKey);
      continue;
    }

    const previousDefinition = data.existingDefinitions?.[placeholderKey];
    const runtimeVariable = SQL_TEMPLATE_VARIABLE_BY_MAPPED[mappedVariable];

    inputVariableDefinitions[placeholderKey] = {
      name: placeholderKey,
      type: previousDefinition?.type ?? runtimeVariable.type,
      mappedVariable,
      ...(previousDefinition?.description
        ? { description: previousDefinition.description }
        : { description: runtimeVariable.description }),
    };
  }

  return {
    placeholderKeys,
    unknownPlaceholderKeys,
    inputVariableDefinitions,
  };
}
